import { axiosInstance as axios } from '3-foundation/ContentApi/axiosInstance';
import logger from '3-foundation/Logging/logger';
import Application from '3-foundation/Application/ApplicationEntity';
import { AxiosRequestConfig } from 'axios';
import { getEngineId } from './getEngineId';
import { getSearchSiteId } from './getSearchSiteId';

type FieldName =
	| 'Category'
	| 'Content'
	| 'CrawlDepth_number'
	| 'Description'
	| 'DomainName'
	| 'HistoricalDocumentChecksum'
	| 'HistoricalIndexedAtArchive_date'
	| 'HistoricalIndexedAtInitial_date'
	| 'Host'
	| 'Id'
	| 'IndexedAt'
	| 'ParentUrl'
	| 'Title'
	| 'Url'
	| 'search-breadcrumb'
	| 'search-locale'
	| 'search-page-type'
	| 'search-parent-id'
	| 'search-site'
	| 'search-tags-subject'
	| 'search-tags-status'
	| 'search-tags-type'
	| 'search-date'
	| 'search-end-date'
	| 'search-kicker'
	| 'search-rte-teaser'
	| 'search-price'
	| 'search-location'
	| 'search-sort'
	| 'search-title';

export interface SearchResultResponse {
	TypedDocuments: {
		ResultIndex: number;
		Sortings: unknown;
		Score: number;
		TitleHasHighlight: boolean;
		Fields: {
			[fieldName in FieldName]: {
				Field: fieldName;
				Highlights: unknown[];
				isArray: boolean;
				Value: string;
				Values: string[];
			};
		};
		FieldNames: FieldName;
	}[];
	TopHits: [];
	TotalDocument: number;
	Suggestions: [];
	Facets: {
		[fieldName in FieldName]: {
			FieldName: fieldName;
			Field: fieldName;
			MissingCount: number;
			AllCount: number;
			Items: {
				Key: string;
				Count: number;
				Facets: unknown;
			}[];
		};
	};
	Banners: unknown;
	FixedQuery: string;
	ResponseTime: number;
	Synonyms: unknown;
	QueryId: string;
	GenerativeAnswerAvailable: boolean;
}
export interface QueryCludoRequestInterface {
	filters: {
		parentId?: string;
		pageType?: string;
		tagsDate?: string[];
		tagsSubject?: string[];
		tagsStatus?: string[];
		tagsType?: string[];
		fromDate?: string;
		toDate?: string;
	};
	limit?: number;
	sort?: { [fieldName in FieldName]?: 'asc' | 'desc' };
	locale: string;
	siteId: string;
}

export const queryCludo = async ({ filters, limit, sort, locale, siteId }: QueryCludoRequestInterface): Promise<SearchResultResponse> => {
	const profiler = logger.startTimer();
	const clientId = Application.envVars.NEXT_PUBLIC_SEARCH_CLUDO_CLIENT_ID;

	try {
		const engineId = getEngineId({
			daEngine: Application.envVars.NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_DA as string,
			enEngine: Application.envVars.NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_EN as string,
			klEngine: Application.envVars.NEXT_PUBLIC_SEARCH_CLUDO_ENGINE_KL as string,
			locale: locale as 'da' | 'en' | 'kl-gl',
		});

		if (!clientId || !engineId) {
			throw new Error(`Missing Cludo configuration: clientId=${clientId}, engineId=${engineId}`);
		}

		const authKey = btoa(`${clientId}:${engineId}:SearchKey`);

		const requestOptions: AxiosRequestConfig = {
			url: encodeURI(`https://api-eu1.cludo.com/api/v3/${clientId}/${engineId}/search`),
			method: 'POST',
			headers: {
				Authorization: `SiteKey ${authKey}`,
			},
			data: {
				query: '*',
				operator: 'or',
				filters: {
					'search-site': ['Value', getSearchSiteId(siteId)],
					...(filters?.pageType && {
						'search-page-type': ['Value', filters.pageType],
					}),
					...(filters?.tagsDate?.length && {
						'search-date-year-month': ['Values', ...filters.tagsDate],
					}),
					...(filters?.tagsSubject?.length && {
						'search-tags-subject': ['Values', ...filters.tagsSubject],
					}),
					...(filters?.tagsStatus?.length && {
						'search-tags-status': ['Values', ...filters.tagsStatus],
					}),
					...(filters?.tagsType?.length && {
						'search-tags-type': ['Values', ...filters.tagsType],
					}),
					// If site id has been overridden then don't filter on the parent id, as it wont exist in the index
					// We just get all entries in the index
					...(!Application?.envVars?.NEXT_PUBLIC_SEARCH_SITE_ID_OVERRIDE && {
						'search-parent-id': ['Value', filters?.parentId],
					}),
					...((filters?.fromDate || filters?.toDate) && {
						date: ['search-end-date_date', filters?.fromDate ? filters.fromDate : '', filters?.toDate ? filters.toDate : ''],
					}),
				},
				notFilters: {},
				postFilters: {},
				postFiltersOperator: 'or',
				enableFacetFiltering: false,
				enableRelatedSearches: false,
				sort: sort,
				valueBoost: [],
				responseType: 'JsonObject',
				perPage: limit,
			},
		};

		const response = await axios(requestOptions);
		const { data } = response ?? {};
		if (data?.errors) throw data?.errors;
		return data;
	} catch (error) {
		throw new Error(
			`Request options in search connector are likely miss configured. Filter subject tags: ${filters?.tagsSubject?.length ? filters.tagsSubject.join(', ') : 'None'} \nCause: ${error}`,
			error,
		);
	} finally {
		profiler.done({ message: 'search', level: 'debug' });
	}
};
